import * as Dialog from "@radix-ui/react-dialog";
import { ExternalLink, Loader2, X } from "lucide-react";
import {
	type EngineeringRelatedDocumentsParams,
	useEngineeringRelatedDocuments,
} from "@/lib/engineering-insights/api";
import { Button, ErrorBlock } from "@/components/ui-ext/primitives";

const text = (value: unknown, fallback = "—"): string =>
	typeof value === "string" && value.trim() ? value : fallback;

const numberText = (value: unknown): string =>
	typeof value === "number" && Number.isFinite(value) ? String(value) : "—";

function WorkItemRow({ document }: { document: Record<string, unknown> }) {
	return (
		<div className="rounded-[12px] border border-soft p-3">
			<div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-anchor">
				<span>{text(document.key)}</span>
				<span className="text-xs font-medium text-muted-foreground">{text(document.issueType)}</span>
				<span className="text-xs font-medium text-muted-foreground">{text(document.statusName)}</span>
			</div>
			<div className="mt-1 text-sm text-anchor">{text(document.summary)}</div>
			<div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
				<span>Squad: {text(document.squad)}</span>
				<span>Engineer: {text(document.assigneeIdentifier)}</span>
				<span>Points: {numberText(document.storyPoints)}</span>
				<span>Resolved: {text(document.resolvedAt)}</span>
			</div>
		</div>
	);
}

function PullRequestRow({ document }: { document: Record<string, unknown> }) {
	const repo = text(document.repo);
	const number = numberText(document.number);
	const url =
		repo !== "—" && number !== "—" ? `https://github.com/${repo}/pull/${number}` : undefined;
	return (
		<div className="rounded-[12px] border border-soft p-3">
			<div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-anchor">
				<span>{repo}#{number}</span>
				{url && (
					<a href={url} target="_blank" rel="noreferrer" className="text-[var(--primary)]">
						<ExternalLink className="h-3.5 w-3.5" />
					</a>
				)}
			</div>
			<div className="mt-1 text-sm text-anchor">{text(document.title)}</div>
			<div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
				<span>Author: {text(document.author)}</span>
				<span>Squad: {text(document.squad)}</span>
				<span>Review rounds: {numberText(document.reviewRounds)}</span>
				<span>Merged: {text(document.mergedAt)}</span>
			</div>
		</div>
	);
}

function AiUsageRow({ document }: { document: Record<string, unknown> }) {
	return (
		<div className="rounded-[12px] border border-soft p-3">
			<div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-anchor">
				<span>{text(document.engineer ?? document.user)}</span>
				<span className="text-xs font-medium text-muted-foreground">{text(document.metricName)}</span>
			</div>
			<div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
				<span>Model: {text(document.model)}</span>
				<span>Squad: {text(document.squad)}</span>
				<span>Units: {numberText(document.usageUnits)}</span>
				<span>Cost: {numberText(document.estimatedCostUsd)}</span>
				<span>Date: {text(document.date)}</span>
			</div>
		</div>
	);
}

function DocumentRow({ document }: { document: Record<string, unknown> }) {
	if (document.type === "pull_request") {
		return <PullRequestRow document={document} />;
	}
	if (document.type === "ai_usage_daily") {
		return <AiUsageRow document={document} />;
	}
	return <WorkItemRow document={document} />;
}

export function RelatedDocumentsModal({
	open,
	onOpenChange,
	params,
	title,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	params: EngineeringRelatedDocumentsParams | null;
	title: string;
}) {
	const query = useEngineeringRelatedDocuments(params, open);
	const documents = query.data?.documents ?? [];

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/30" />
				<Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[82vh] w-[min(820px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-[16px] border border-soft bg-surface shadow-card">
					<div className="flex items-start justify-between gap-4 border-b border-soft p-5">
						<div>
							<Dialog.Title className="text-base font-semibold text-anchor">
								Related data points
							</Dialog.Title>
							<Dialog.Description className="mt-1 text-sm text-muted-foreground">
								Raw source documents used for {title}.
							</Dialog.Description>
						</div>
						<Dialog.Close asChild>
							<Button variant="ghost" size="sm" aria-label="Close modal">
								<X className="h-4 w-4" />
							</Button>
						</Dialog.Close>
					</div>
					<div className="min-h-0 flex-1 overflow-y-auto p-5">
						{query.isLoading ? (
							<div className="grid min-h-40 place-items-center text-sm text-muted-foreground">
								<div className="inline-flex items-center gap-2">
									<Loader2 className="h-4 w-4 animate-spin" />
									Loading related documents...
								</div>
							</div>
						) : query.isError ? (
							<ErrorBlock
								message={
									query.error instanceof Error
										? query.error.message
										: "Failed to load related documents."
								}
								onRetry={() => void query.refetch()}
							/>
						) : documents.length === 0 ? (
							<div className="rounded-[12px] border border-dashed border-soft p-8 text-center text-sm text-muted-foreground">
								No related raw documents were found for this selection.
							</div>
						) : (
							<div className="flex flex-col gap-3">
								{documents.map((document) => (
									<DocumentRow key={document.id} document={document} />
								))}
							</div>
						)}
					</div>
					<div className="border-t border-soft px-5 py-3 text-xs text-muted-foreground">
						Showing {documents.length} of {query.data?.total ?? documents.length} documents
						{query.data?.sourceType ? ` (${query.data.sourceType.replace(/_/g, " ")})` : ""}.
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

